/**
 * Weather rendering: vanilla-style camera-centred rain / snow, splash droplets
 * and lightning bolts.
 *
 * Rain and snow are one camera-facing strip per column in a (2R+1)^2 window
 * around the camera. Each strip runs from the column's rain heightmap (the top
 * of the highest solid / liquid block, so roofs and trees stop it) up to R
 * blocks over the camera, the texture scrolls down it and the strip fades out
 * with distance. The strips are rebuilt into fixed buffers every frame and the
 * heightmap / biome per column is cached, refreshed a few columns per frame, so
 * nothing allocates after construction.
 *
 * Colours are linear radiance (the scene renders to an HDR target).
 */

import * as THREE from 'three';
import { WORLD_HEIGHT } from '../constants';
import type { Biome } from '../worldgen/biomes';
import { PRECIP_NONE, PRECIP_RAIN, PRECIP_SNOW, precipitationAt } from '../worldgen/biomeClimate';
import { weather, type Weather } from '../game/weather';
import { columnTop } from '../game/weatherWorld';
import type { World } from '../world/chunk';
import type { EnvUniforms } from './env';
import { RAIN_SOUND_RADIUS, RAIN_TICK, rainSoundFor, rainTickChance } from '../audio/rainSound';
import { easeWetness } from './wetness';

/** Vanilla draws 10 columns each way (fancy); phones take less. */
export const RAIN_RADIUS_MAX = 10;
/** Blocks of strip texture per repeat. */
const TEX_BLOCKS = 4;
/** Columns newly entering the window that may be resolved per frame, and re-validated per frame. */
const FRESH_BUDGET = 64;
const REVALIDATE = 24;
const SPLASH_POOL = 96;
const SPLASH_LIFE = 0.32;
const BOLT_SEGMENTS = 18;
const BOLT_LIFE = 0.45;

/** The real vanilla environment/rain.png and snow.png strips. */
function makeTexture(kind: 'rain' | 'snow'): THREE.Texture {
  const t = new THREE.TextureLoader().load(`mc/environment/${kind}.png`);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.NearestMipmapLinearFilter;
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** One strip mesh with a fixed quad budget. */
class Strips {
  readonly mesh: THREE.Mesh;
  count = 0;
  readonly pos: Float32Array;
  readonly uv: Float32Array;
  readonly col: Float32Array;
  private readonly geom = new THREE.BufferGeometry();

  constructor(quads: number, tex: THREE.Texture) {
    this.pos = new Float32Array(quads * 12);
    this.uv = new Float32Array(quads * 8);
    this.col = new Float32Array(quads * 16);
    const idx = new Uint16Array(quads * 6);
    for (let q = 0; q < quads; q++) {
      const v = q * 4;
      idx.set([v, v + 1, v + 2, v, v + 2, v + 3], q * 6);
    }
    const dyn = (a: Float32Array, n: number): THREE.BufferAttribute => new THREE.BufferAttribute(a, n).setUsage(THREE.DynamicDrawUsage);
    this.geom.setAttribute('position', dyn(this.pos, 3));
    this.geom.setAttribute('uv', dyn(this.uv, 2));
    this.geom.setAttribute('color', dyn(this.col, 4));
    this.geom.setIndex(new THREE.BufferAttribute(idx, 1));
    this.geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    const mat = new THREE.MeshBasicMaterial({
      map: tex, vertexColors: true, transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false,
    });
    this.mesh = new THREE.Mesh(this.geom, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 10;
    this.mesh.visible = false;
  }

  flush(): void {
    this.geom.setDrawRange(0, this.count * 6);
    (this.geom.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (this.geom.attributes.uv as THREE.BufferAttribute).needsUpdate = true;
    (this.geom.attributes.color as THREE.BufferAttribute).needsUpdate = true;
    this.mesh.visible = this.count > 0;
  }
}

/** Anything that can play a sound / loop (the AudioManager). */
interface AudioLike {
  loop(id: string): { setLevel(v: number): void; stop(): void };
  play(id: string, opts?: unknown): unknown;
}

export class WeatherRenderer {
  readonly group = new THREE.Group();
  /** Columns each way from the camera: lower on phones. */
  radius = 8;
  /** Hook: audio manager for the rain bed and thunder. */
  audio: AudioLike | null = null;

  private readonly rain: Strips;
  private readonly snow: Strips;
  private readonly splash: THREE.Points;
  private readonly splashPos: Float32Array;
  private readonly splashVel = new Float32Array(SPLASH_POOL * 3);
  private readonly splashAge = new Float32Array(SPLASH_POOL).fill(SPLASH_LIFE);
  private splashCursor = 0;
  private splashAcc = 0;
  private readonly bolt: THREE.Mesh;
  private readonly boltPos: Float32Array;
  private boltAge = BOLT_LIFE;
  private rainLoop: { setLevel(v: number): void; stop(): void } | null = null;
  private thunderQueue: number[] = []; // seconds until each thunderclap
  private rnd = 1234567;

  // per-column cache, ring-indexed by (x mod G, z mod G)
  private readonly G = RAIN_RADIUS_MAX * 2 + 1;
  private readonly colX: Int32Array;
  private readonly colZ: Int32Array;
  private readonly colTop: Int16Array;
  private readonly colBiome: Uint8Array;
  private readonly colType: Uint8Array;
  private revalCursor = 0;
  private clock = 0;

  constructor(
    private readonly world: World,
    private readonly env: EnvUniforms,
    private readonly biomeAt: (x: number, z: number) => Biome,
    private readonly w: Weather = weather,
  ) {
    const cols = this.G * this.G;
    this.colX = new Int32Array(cols).fill(0x7fffffff);
    this.colZ = new Int32Array(cols);
    this.colTop = new Int16Array(cols);
    this.colBiome = new Uint8Array(cols);
    this.colType = new Uint8Array(cols);
    this.rain = new Strips(cols, makeTexture('rain'));
    this.snow = new Strips(cols, makeTexture('snow'));
    this.group.add(this.rain.mesh, this.snow.mesh);

    this.splashPos = new Float32Array(SPLASH_POOL * 3);
    const sg = new THREE.BufferGeometry();
    sg.setAttribute('position', new THREE.BufferAttribute(this.splashPos, 3).setUsage(THREE.DynamicDrawUsage));
    sg.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this.splash = new THREE.Points(sg, new THREE.PointsMaterial({
      color: new THREE.Color(0.55, 0.65, 0.85), size: 0.09, sizeAttenuation: true, transparent: true, opacity: 0.7,
      depthWrite: false, fog: false,
    }));
    this.splash.frustumCulled = false;
    this.splash.visible = false;
    this.group.add(this.splash);

    // bolt: BOLT_SEGMENTS pieces, each two crossed quads
    this.boltPos = new Float32Array(BOLT_SEGMENTS * 8 * 3);
    const bg = new THREE.BufferGeometry();
    bg.setAttribute('position', new THREE.BufferAttribute(this.boltPos, 3).setUsage(THREE.DynamicDrawUsage));
    const bi = new Uint16Array(BOLT_SEGMENTS * 12);
    for (let s = 0; s < BOLT_SEGMENTS * 2; s++) bi.set([s * 4, s * 4 + 1, s * 4 + 2, s * 4, s * 4 + 2, s * 4 + 3], s * 6);
    bg.setIndex(new THREE.BufferAttribute(bi, 1));
    bg.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this.bolt = new THREE.Mesh(bg, new THREE.MeshBasicMaterial({
      color: new THREE.Color(5, 5, 6.5), transparent: true, opacity: 1, depthWrite: false, side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending, fog: false,
    }));
    this.bolt.frustumCulled = false;
    this.bolt.visible = false;
    this.bolt.renderOrder = 11;
    this.group.add(this.bolt);
  }

  private rand(): number {
    this.rnd = (Math.imul(this.rnd, 1664525) + 1013904223) >>> 0;
    return this.rnd / 4294967296;
  }

  /** A bolt at a block position: geometry, flash and (delayed by distance) thunder. */
  strike(x: number, y: number, z: number, camX: number, camY: number, camZ: number): void {
    this.rnd = (Math.imul(x, 73856093) ^ Math.imul(z, 19349663) ^ 0x9e3779b9) >>> 0;
    let px = x + 0.5, pz = z + 0.5;
    const top = WORLD_HEIGHT + 60;
    const stepY = (top - y) / BOLT_SEGMENTS;
    const hw = 0.3;
    let n = 0;
    // build from the top down so the jitter ends exactly on the strike point
    const xs: number[] = [], zs: number[] = [];
    for (let i = 0; i <= BOLT_SEGMENTS; i++) {
      xs.push(px); zs.push(pz);
      px += (this.rand() - 0.5) * 6; pz += (this.rand() - 0.5) * 6;
    }
    const dx = xs[BOLT_SEGMENTS] - (x + 0.5), dz = zs[BOLT_SEGMENTS] - (z + 0.5);
    for (let i = 0; i <= BOLT_SEGMENTS; i++) {
      const k = i / BOLT_SEGMENTS; // pull the end onto the target
      xs[i] -= dx * k; zs[i] -= dz * k;
    }
    for (let i = 0; i < BOLT_SEGMENTS; i++) {
      const y0 = top - i * stepY, y1 = y0 - stepY;
      const x0 = xs[i], z0 = zs[i], x1 = xs[i + 1], z1 = zs[i + 1];
      const p = this.boltPos;
      // plane along x, plane along z
      p.set([x0 - hw, y0, z0, x0 + hw, y0, z0, x1 + hw, y1, z1, x1 - hw, y1, z1], n); n += 12;
      p.set([x0, y0, z0 - hw, x0, y0, z0 + hw, x1, y1, z1 + hw, x1, y1, z1 - hw], n); n += 12;
    }
    (this.bolt.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    this.boltAge = 0;
    // vanilla thunder: distance / speed of sound (~340 m/s, shortened) after the flash
    const d = Math.hypot(x - camX, y - camY, z - camZ);
    this.thunderQueue.push(Math.min(3, d / 100));
    this.audio?.play('entity.lightning_bolt.impact', { pos: { x, y, z }, volume: 1.5 });
  }

  /** dt seconds; camera position drives the column window. Call once per frame. */
  update(dt: number, cam: THREE.Vector3, overworld: boolean): void {
    const w = this.w;
    this.clock += dt;
    const strength = overworld ? w.rainStrength() : 0;
    // ground wetness follows rain only where it actually rains (not desert / snow / Nether)
    const bx = Math.floor(cam.x), bz = Math.floor(cam.z);
    const rainsHere = overworld && strength > 0 && precipitationAt(this.biomeAt(bx, bz), cam.y) === PRECIP_RAIN;
    const wu = this.env.uWetness;
    wu.value = overworld ? easeWetness(wu.value, rainsHere ? strength : 0, dt) : 0;
    this.updateAudio(dt, strength, cam);
    this.updateBolt(dt, overworld);
    if (strength < 0.01) {
      this.rain.count = 0; this.snow.count = 0;
      this.rain.flush(); this.snow.flush();
      this.splash.visible = false;
      return;
    }
    this.buildStrips(cam, strength);
    this.updateSplash(dt, cam, strength);
  }

  private resolve(i: number, x: number, z: number): void {
    const top = columnTop(this.world, x, z);
    this.colX[i] = top < 0 ? 0x7fffffff : x; // unloaded: retry next frame
    this.colZ[i] = z;
    this.colTop[i] = Math.max(0, top);
    if (top < 0) { this.colType[i] = PRECIP_NONE; return; }
    const b = this.biomeAt(x, z);
    this.colBiome[i] = b;
    this.colType[i] = precipitationAt(b, top);
  }

  private buildStrips(cam: THREE.Vector3, strength: number): void {
    const R = this.radius, G = this.G;
    const cx = Math.floor(cam.x), cz = Math.floor(cam.z), cy = Math.floor(cam.y);
    const minWinY = cy - R, maxWinY = cy + R;
    const rain = this.rain, snow = this.snow;
    rain.count = 0; snow.count = 0;
    let fresh = FRESH_BUDGET;
    // brightness: rain is a lit particle, dim at night
    const lit = 0.16 + 0.84 * this.env.uDayLight.value;
    const t = this.clock;
    const R2 = R * R;
    for (let dz = -R; dz <= R; dz++) {
      for (let dx = -R; dx <= R; dx++) {
        const d2 = dx * dx + dz * dz;
        if (d2 > R2) continue;
        const x = cx + dx, z = cz + dz;
        const i = (((x % G) + G) % G) + (((z % G) + G) % G) * G;
        if (this.colX[i] !== x || this.colZ[i] !== z) {
          if (fresh <= 0) continue;
          fresh--;
          this.resolve(i, x, z);
        }
        const type = this.colType[i];
        if (type === PRECIP_NONE) continue;
        const minY = Math.max(this.colTop[i], minWinY);
        if (minY >= maxWinY) continue;
        const px = x + 0.5 - cam.x, pz = z + 0.5 - cam.z;
        const len = Math.sqrt(px * px + pz * pz);
        if (len < 0.05) continue;
        const nx = -pz / len * 0.5, nz = px / len * 0.5;
        const s = type === PRECIP_RAIN ? rain : snow;
        const q = s.count++;
        const p = q * 12, u = q * 8, c = q * 16;
        const wx = x + 0.5, wz = z + 0.5;
        s.pos[p] = wx - nx; s.pos[p + 1] = maxWinY; s.pos[p + 2] = wz - nz;
        s.pos[p + 3] = wx + nx; s.pos[p + 4] = maxWinY; s.pos[p + 5] = wz + nz;
        s.pos[p + 6] = wx + nx; s.pos[p + 7] = minY; s.pos[p + 8] = wz + nz;
        s.pos[p + 9] = wx - nx; s.pos[p + 10] = minY; s.pos[p + 11] = wz - nz;
        // per-column random phase (vanilla hashes the column); the texture falls with time
        const h = ((Math.imul(x, 3129871) ^ Math.imul(z, 116129781)) >>> 0) / 4294967296;
        const fall = type === PRECIP_RAIN ? t * 3.2 : t * 0.5;
        const sway = type === PRECIP_SNOW ? Math.sin(t * 0.6 + h * 6.28) * 0.15 : 0;
        const u0 = h + sway, u1 = u0 + 1;
        const vTop = maxWinY / TEX_BLOCKS + h * 3 + fall, vBot = minY / TEX_BLOCKS + h * 3 + fall;
        s.uv[u] = u0; s.uv[u + 1] = vTop;
        s.uv[u + 2] = u1; s.uv[u + 3] = vTop;
        s.uv[u + 4] = u1; s.uv[u + 5] = vBot;
        s.uv[u + 6] = u0; s.uv[u + 7] = vBot;
        // vanilla: alpha = ((1 - d^2) * 0.5 + 0.5) * strength, d the distance over the radius
        const dn = d2 / R2;
        const a = ((1 - dn) * 0.5 + 0.5) * strength * (type === PRECIP_RAIN ? 0.85 : 0.95);
        const tr = type === PRECIP_RAIN ? 0.62 : 0.9, tg = type === PRECIP_RAIN ? 0.7 : 0.92, tb = type === PRECIP_RAIN ? 0.88 : 0.95;
        for (let k = 0; k < 4; k++) {
          s.col[c + k * 4] = tr * lit; s.col[c + k * 4 + 1] = tg * lit; s.col[c + k * 4 + 2] = tb * lit; s.col[c + k * 4 + 3] = a;
        }
      }
    }
    // keep edits (a roof placed, a hole dug) honest: re-resolve a few cached columns each frame
    const n = G * G;
    for (let k = 0; k < REVALIDATE; k++) {
      const i = this.revalCursor;
      this.revalCursor = (i + 1) % n;
      const x = this.colX[i];
      if (x === 0x7fffffff) continue;
      const z = this.colZ[i];
      const top = columnTop(this.world, x, z);
      if (top < 0) continue;
      this.colTop[i] = top;
      this.colType[i] = precipitationAt(this.colBiome[i] as Biome, top);
    }
    rain.flush(); snow.flush();
  }

  /** Tiny droplets bouncing off the ground inside the rain window. */
  private updateSplash(dt: number, cam: THREE.Vector3, strength: number): void {
    const R = Math.min(this.radius, 7), G = this.G;
    this.splashAcc += dt * 70 * strength;
    const cx = Math.floor(cam.x), cz = Math.floor(cam.z);
    while (this.splashAcc >= 1) {
      this.splashAcc -= 1;
      const x = cx + Math.floor((this.rand() * 2 - 1) * R), z = cz + Math.floor((this.rand() * 2 - 1) * R);
      const i = (((x % G) + G) % G) + (((z % G) + G) % G) * G;
      if (this.colX[i] !== x || this.colZ[i] !== z || this.colType[i] !== PRECIP_RAIN) continue;
      const k = this.splashCursor;
      this.splashCursor = (k + 1) % SPLASH_POOL;
      this.splashPos[k * 3] = x + this.rand(); this.splashPos[k * 3 + 1] = this.colTop[i] + 0.04; this.splashPos[k * 3 + 2] = z + this.rand();
      this.splashVel[k * 3] = (this.rand() - 0.5) * 0.8; this.splashVel[k * 3 + 1] = 1.6 + this.rand() * 1.4; this.splashVel[k * 3 + 2] = (this.rand() - 0.5) * 0.8;
      this.splashAge[k] = 0;
    }
    let any = false;
    for (let k = 0; k < SPLASH_POOL; k++) {
      if (this.splashAge[k] >= SPLASH_LIFE) { this.splashPos[k * 3 + 1] = -1000; continue; }
      any = true;
      this.splashAge[k] += dt;
      this.splashVel[k * 3 + 1] -= 18 * dt;
      this.splashPos[k * 3] += this.splashVel[k * 3] * dt;
      this.splashPos[k * 3 + 1] += this.splashVel[k * 3 + 1] * dt;
      this.splashPos[k * 3 + 2] += this.splashVel[k * 3 + 2] * dt;
    }
    (this.splash.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    this.splash.visible = any;
  }

  /** The bolt flickers for BOLT_LIFE seconds and is gone. */
  private updateBolt(dt: number, overworld: boolean): void {
    if (this.boltAge >= BOLT_LIFE || !overworld) { this.bolt.visible = false; return; }
    this.boltAge += dt;
    // vanilla flickers: bright, off, bright, fading
    const k = this.boltAge / BOLT_LIFE;
    const on = k < 0.25 || (k > 0.4 && k < 0.7);
    this.bolt.visible = on;
    (this.bolt.material as THREE.MeshBasicMaterial).opacity = 1 - k * 0.6;
  }

  private rainTickAcc = 0;
  /** Vanilla-style: sample rain drops at random exposed columns around the player. */
  private rainDrops(dt: number, strength: number, cam: THREE.Vector3, a: NonNullable<WeatherRenderer['audio']>): void {
    this.rainTickAcc = Math.min(this.rainTickAcc + dt, 0.25);
    const chance = rainTickChance(strength);
    const exposed = this.w.exposed(cam.x, cam.y, cam.z);
    while (this.rainTickAcc >= RAIN_TICK) {
      this.rainTickAcc -= RAIN_TICK;
      if (Math.random() >= chance) continue;
      const x = Math.floor(cam.x + (Math.random() * 2 - 1) * RAIN_SOUND_RADIUS);
      const z = Math.floor(cam.z + (Math.random() * 2 - 1) * RAIN_SOUND_RADIUS);
      const top = columnTop(this.world, x, z);
      if (top < 0) continue;
      const k = this.w.precipAt(x, top, z);
      const precip = k === PRECIP_RAIN ? 'rain' : k === PRECIP_SNOW ? 'snow' : 'none';
      const c = rainSoundFor(precip, exposed, top, cam.y);
      if (c) a.play(c.id, { pos: { x: x + 0.5, y: top, z: z + 0.5 }, volume: c.volume, pitch: c.pitch });
    }
  }

  private updateAudio(dt: number, strength: number, cam: THREE.Vector3): void {
    const a = this.audio;
    if (!a) return;
    if (strength > 0.02) {
      if (!this.rainLoop) this.rainLoop = a.loop('weather.rain.bed');
      this.rainLoop.setLevel(Math.min(1, strength));
      this.rainDrops(dt, strength, cam, a);
    } else if (this.rainLoop) {
      this.rainLoop.stop();
      this.rainLoop = null;
    }
    for (let i = this.thunderQueue.length - 1; i >= 0; i--) {
      this.thunderQueue[i] -= dt;
      if (this.thunderQueue[i] <= 0) {
        a.play('entity.lightning_bolt.thunder', { pos: { x: cam.x, y: cam.y, z: cam.z } });
        this.thunderQueue.splice(i, 1);
      }
    }
  }
}
